/**
 * The part of a Sentry event the noise checks read. Structural, so the helper does not depend on the SDK's types.
 */
export interface SentryExceptionEvent {
  exception?: {
    values?: {
      value?: string;
      stacktrace?: { frames?: { abs_path?: string; filename?: string }[] };
    }[];
  };
}

/**
 * Google Maps loads its parts (`overlay`, `marker`, `places`, …) on demand from its own CDN. When that request fails —
 * a dropped connection, an ad/privacy blocker, an outdated browser (Windows 7 / Chrome 140 in RE-2497) — Maps throws
 * `Error: Could not load "overlay".` from the `onerror` handler of its own `<script>`, inside its `main.js`. No code
 * of ours is on that stack, so nothing here can catch it, and the map just stays without that part.
 *
 * Both have to hold: the message is exactly Maps' module-load failure, and the innermost frame is Google Maps' script.
 * Our own error with the same wording still reports — and a rejected `importLibrary` that our code awaits is caught
 * where it is awaited (RE-1076), not here.
 */
const MAPS_MODULE_LOAD_FAILURE = /^Could not load "[\w-]+"\.?$/;
const MAPS_SCRIPT = /\/maps-api-v3\/api\/js\//;

export const isGoogleMapsModuleLoadFailure = (event: SentryExceptionEvent): boolean => {
  const exception = event.exception?.values?.[0];

  if (!exception?.value || !MAPS_MODULE_LOAD_FAILURE.test(exception.value)) {
    return false;
  }

  // Sentry lists frames oldest first: the last one is where the error was thrown.
  const frames = exception.stacktrace?.frames ?? [];
  const innermost = frames[frames.length - 1];
  const source = innermost?.abs_path || innermost?.filename || "";

  return MAPS_SCRIPT.test(source);
};
